// ADR-132/133: Job Position Mappings overview — one card per provider with a
// summary of its list, opening that provider's list on click.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { Typography, Alert } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Container, Header, HeaderLeft, Title, Content, MainSection, BackButton } from '../../common/styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import {
  INGESTION_PROVIDER_OPTIONS,
  INGESTION_MAPPING_OUTCOMES,
  INGESTION_PATTERN_MATCH_TYPES,
  JOB_POSITION_MAPPINGS_PATH,
} from '../../../constants/ingestion';
import { ingestionConfigurationApi } from '../../../services/api/ingestionConfigurationApi';
import { needsAttention } from './unmappedTitleStatus';
import {
  ProviderCard, ProviderCardHeader, StatGrid, Stat, StatValue, StatLabel, CardFooter,
} from './styles';

const summarize = (set, unmapped) => {
  const mappings = set?.mappings || [];
  const titles = unmapped?.titles || [];
  return {
    version: set?.version ?? 0,
    count: set?.count ?? mappings.length,
    noPositionCount: mappings.filter((m) => m.outcome === INGESTION_MAPPING_OUTCOMES.NO_POSITION).length,
    patternCount: mappings.filter((m) => m.matchType && m.matchType !== INGESTION_PATTERN_MATCH_TYPES.EXACT).length,
    hasCatchAll: mappings.some((m) => m.matchType === INGESTION_PATTERN_MATCH_TYPES.CATCH_ALL),
    updatedAt: set?.version > 0 ? set?.updatedAt : null,
    updatedBy: set?.updatedBy || null,
    lastRunAt: unmapped?.runStartedAt || null,
    importsSince: (unmapped?.scopedRuns || []).length,
    unmappedOpen: unmapped ? titles.filter(needsAttention).length : null,
  };
};

const loadProvider = async (provider) => {
  const [set, unmapped] = await Promise.allSettled([
    ingestionConfigurationApi.getJobPositionMappings(provider),
    ingestionConfigurationApi.getUnmappedTitles(provider),
  ]);
  if (set.status === 'rejected') {
    return { error: set.reason?.message || 'Failed to load' };
  }
  return { summary: summarize(set.value, unmapped.status === 'fulfilled' ? unmapped.value : null) };
};

const SummaryCard = ({ option, state, onOpen }) => {
  const summary = state?.summary;
  return (
    <ProviderCard role="button" tabIndex={0} onClick={onOpen} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}>
      <ProviderCardHeader>
        <Typography variant="h6">{option.label}</Typography>
        <Typography variant="body2" color="textSecondary">{`Read by the ${option.syncHint}`}</Typography>
      </ProviderCardHeader>

      {!state && <LoadingSpinner />}
      {state?.error && <Alert severity="error">{state.error}</Alert>}
      {summary && (
        <>
          <StatGrid>
            <Stat>
              <StatValue>{summary.count}</StatValue>
              <StatLabel>mappings</StatLabel>
            </Stat>
            <Stat>
              <StatValue>{summary.noPositionCount}</StatValue>
              <StatLabel>no position</StatLabel>
            </Stat>
            <Stat>
              <StatValue>{summary.patternCount}</StatValue>
              <StatLabel>patterns</StatLabel>
            </Stat>
            <Stat>
              <StatValue>{`v${summary.version}`}</StatValue>
              <StatLabel>version</StatLabel>
            </Stat>
            <Stat $warning={summary.unmappedOpen > 0}>
              <StatValue>{summary.unmappedOpen ?? '—'}</StatValue>
              <StatLabel>
                {summary.lastRunAt
                  ? `unmapped in last full run (${new Date(summary.lastRunAt).toLocaleDateString()})`
                  : 'unmapped — no full run recorded yet'}
                {summary.importsSince > 0
                  ? ` + ${summary.importsSince} ${summary.importsSince === 1 ? 'import' : 'imports'} since`
                  : ''}
              </StatLabel>
            </Stat>
          </StatGrid>

          {summary.hasCatchAll && (
            <Alert severity="warning" sx={{ py: 0 }}>Has a catch-all (*): no title falls through to automatic matching.</Alert>
          )}

          <CardFooter>
            <span>
              {summary.updatedAt
                ? `Last changed by ${summary.updatedBy} on ${new Date(summary.updatedAt).toLocaleString()}`
                : 'No changes yet'}
            </span>
          </CardFooter>
        </>
      )}
    </ProviderCard>
  );
};

const JobPositionMappingsOverview = () => {
  const navigate = useNavigate();
  const [states, setStates] = useState({});

  useEffect(() => {
    let cancelled = false;
    INGESTION_PROVIDER_OPTIONS.forEach((option) => {
      loadProvider(option.value).then((state) => {
        if (!cancelled) {
          setStates((prev) => ({ ...prev, [option.value]: state }));
        }
      });
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/ingestion')}>
            <FaArrowLeft /> Back to Ingestion
          </BackButton>
          <Title>Job Position Mappings</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <MainSection>
          <Grid container spacing={3}>
            {INGESTION_PROVIDER_OPTIONS.map((option) => (
              <Grid item xs={12} md={6} key={option.value}>
                <SummaryCard
                  option={option}
                  state={states[option.value]}
                  onOpen={() => navigate(`${JOB_POSITION_MAPPINGS_PATH}/${option.slug}`)}
                />
              </Grid>
            ))}
          </Grid>
        </MainSection>
      </Content>
    </Container>
  );
};

export default JobPositionMappingsOverview;
